/**
 * Status, filter, and display config for pairs.
 */
import type { CSSProperties } from 'react';
import {
  CheckCircle2,
  MinusCircle,
  Archive,
  HelpCircle,
  Clock,
  Users,
  User,
  History,
  RefreshCw,
  TrendingDown,
  XCircle,
  Flag,
  Heart,
  HeartHandshake,
  List,
  Scale,
  UserCheck,
  UserX,
  GraduationCap,
  BookOpen,
  FlaskConical,
  type LucideIcon,
} from 'lucide-react';
import type {
  PairStatus,
  PairTabView,
  PairFilterGroupId,
  DissociationReasonCode,
  PairFilters,
} from '@/types/remote/pair.types';
import { STUDENT_SITUATION_CONFIG, TUTOR_SITUATION_CONFIG } from '@/constants/shared-catalog';

export { STUDENT_SITUATION_CONFIG, TUTOR_SITUATION_CONFIG };

export {
  ASSIGNABILITY_CONFIG,
  DATE_RANGE_PERIOD_OPTIONS,
  DATE_RANGE_PERIOD_OPTIONS_FULL,
  DATE_RANGE_PERIOD_OPTIONS_WITH_TODAY,
  GENDER_LABELS,
  PAIR_TYPE_CONFIG,
  SESSION_TYPE_CONFIG,
  STUDENT_GENDER_LABELS,
  type PairTypeConfig,
} from '@/constants/shared-catalog';
import { getRoleLabel, getRoleLabelPlural } from '@/utils/role-label-utils';
import type { BadgeVariant } from '@/components/common/LabelBadge';

/** Badge + icon config for a pair status. */
export interface PairStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor?: string;
  /** CSS color for the connection line between tutor and student (§3) */
  lineColor: string;
}

/** Badge config for each pair status. */
export const PAIR_STATUS_CONFIG: Record<PairStatus, PairStatusConfig> = {
  Active: {
    label: 'Actif',
    icon: CheckCircle2,
    badgeVariant: 'active',
    lineColor: 'rgb(var(--status-success))',
  },
  Inactive: {
    label: 'Sans activité',
    icon: MinusCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-warning))',
    lineColor: 'rgb(var(--status-warning))',
  },
  Archived: {
    label: 'Archivé',
    icon: Archive,
    badgeVariant: 'ghost',
    badgeColor: 'rgb(var(--status-danger))',
    lineColor: 'rgb(var(--status-danger))',
  },
};

/** Display fields for a tutor or student situation badge. */
export interface UserSituationConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor?: string;
}

/** Tutor situation catalog, widened for merge into the unified map. */
const _t = TUTOR_SITUATION_CONFIG as Record<string, UserSituationConfig>;
/** Student situation catalog, widened for merge into the unified map. */
const _s = STUDENT_SITUATION_CONFIG as Record<string, UserSituationConfig>;

/** Merges tutor and student situations. Covers underscore_case (onboarding) and English spaced (legacy pairs API). */
export const USER_SITUATION_CONFIG: Record<string, UserSituationConfig> = {
  ..._t,
  ..._s,
  'active tutoring': _t['tutoring_in_progress'],
  'tutor to assign': _t['awaiting_student'],
  'student to assign': _s['awaiting_tutor'],
  'not applicable': { label: 'Non applicable', icon: MinusCircle, badgeVariant: 'disabled' },
};

/** Compact program badges (HomeClasse, Tutorat solidaire, …). */
export const PROGRAMME_BADGE_CONFIG: Record<string, { label: string; color: string }> = {
  HomeClasse: { label: 'HC', color: 'rgb(var(--color-1-nuance-1))' },
  'Tutorat solidaire': { label: 'TS', color: 'rgb(var(--status-success))' },
  ZUPdeFOOT: { label: 'ZDF', color: 'rgb(139 92 246)' },
  CH: { label: 'CH', color: 'rgb(var(--status-warning))' },
  NC: { label: 'NC', color: 'rgb(var(--status-neutral))' },
};

/** In-person pair tab labels. */
export const PAIR_TAB_LABELS: Record<PairTabView, string> = {
  active: 'Actifs',
  inactive: 'Inactifs',
  archived: 'Archivés',
};

/** Top-level pairs page tabs (list vs matching). */
export type PairsPageTab = 'list' | 'matching';

/** Labels for the pairs page tabs. */
export const PAIRS_TAB_LABELS: Record<PairsPageTab, string> = {
  list: 'Liste des binômes',
  matching: 'Matching',
};

/** Sequential colors for the pairs page tabs. */
export const PAIRS_TAB_COLORS: Record<PairsPageTab, string> = {
  list: 'var(--color-1-nuance-1)',
  matching: 'var(--color-3-nuance-1)',
};

/** Icons for the pairs page tabs. */
export const PAIRS_TAB_ICONS = {
  list: List,
  matching: HeartHandshake,
} as const;

/** Default remote pair list filters. */
export const DEFAULT_PAIR_FILTERS: PairFilters = {
  statuses: [],
  search: undefined,
  dateRange: 'all',
  customDateFrom: undefined,
  customDateTo: undefined,
  coordinatorUserIds: [],
  establishmentId: undefined,
  programs: [],
  schoolYearId: undefined,
};

/** Human-readable label per remote pair filter group. */
export const PAIR_FILTER_LABELS: Record<PairFilterGroupId, string> = {
  'filter-status': 'Statut',
  'filter-search': 'Recherche',
  'filter-period': 'Période',
  'filter-date-range': 'Plage de dates',
  get 'filter-coordinator'() {
    return getRoleLabel('coordinator');
  },
  'filter-managed-by': 'Géré par',
  'filter-establishment': 'Établissement',
  'filter-program': 'Programme',
  'filter-school-year': 'Année scolaire',
};

/** All remote pair filter group ids (configure-filters panel). */
export const ALL_PAIR_FILTER_GROUP_IDS: PairFilterGroupId[] = [
  'filter-status',
  'filter-search',
  'filter-period',
  'filter-date-range',
  'filter-managed-by',
  'filter-establishment',
  'filter-program',
  'filter-school-year',
];

/** Badge + icon config for a pair dissociation reason. */
export interface DissociationReasonConfig {
  code: DissociationReasonCode;
  label: string;
  requiresDescription: boolean;
  badgeVariant: BadgeVariant;
  badgeColor: string;
  icon: LucideIcon;
}

/** Dissociation reason options. */
export const DISSOCIATION_REASONS: DissociationReasonConfig[] = [
  {
    code: 'END_OF_SUPPORT',
    label: "Fin d'accompagnement",
    requiresDescription: false,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-1-nuance-1)',
    icon: Flag,
  },
  {
    code: 'TUTOR_UNAVAILABILITY',
    label: 'Indisponibilité durable du tuteur',
    requiresDescription: false,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-warning))',
    icon: Clock,
  },
  {
    code: 'FAMILY_REQUEST',
    label: 'Demande de la famille',
    requiresDescription: false,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(139 92 246)',
    icon: Users,
  },
  {
    code: 'TUTOR_CHANGE',
    label: 'Changement de tuteur',
    requiresDescription: false,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-1)',
    icon: RefreshCw,
  },
  {
    code: 'STUDENT_SITUATION_CHANGE',
    label: "Changement de situation de l'élève",
    requiresDescription: false,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(245 158 11)',
    icon: TrendingDown,
  },
  {
    code: 'ABANDONMENT',
    label: 'Abandon',
    requiresDescription: false,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-danger))',
    icon: XCircle,
  },
  {
    code: 'OTHER',
    label: 'Autre motif',
    requiresDescription: true,
    badgeVariant: 'ghost',
    badgeColor: 'rgb(var(--status-neutral))',
    icon: HelpCircle,
  },
];

/** Dissociation reasons as {@link Select} options. */
export const DISSOCIATION_REASON_SELECT_OPTIONS = DISSOCIATION_REASONS.map((reason) => ({
  value: reason.code,
  label: reason.label,
}));

/** Impact bullets shown before confirming a dissociation. */
export const DISSOCIATION_IMPACTS = [
  'Les séances passées restent conservées',
  'Les comptes rendus et scorings restent consultables',
  'Le tuteur récupère une capacité',
  "L'élève redevient affectable si les conditions sont remplies",
];

/** Warning shown before confirming an irreversible dissociation. */
export const DISSOCIATION_IRREVERSIBLE_WARNING =
  'Action définitive et irréversible — un binôme archivé ne peut pas être réactivé';

/** Pair detail section labels. */
export const PAIR_SECTION_LABELS: Record<string, string> = {
  information: 'Informations',
  details: 'Détails',
  history: 'Historique',
  status: 'Statut',
  management: 'Gestion',
  dissociation: 'Dissociation',
};

/** Pair detail panel tabs. */
export type PairDetailTab = 'details' | 'history';

/** Tabs on the pair detail view. */
export const PAIR_DETAIL_TAB_CONFIGS: Array<{
  id: PairDetailTab;
  label: string;
  icon: LucideIcon;
}> = [
  { id: 'details', label: 'Détails', icon: User },
  { id: 'history', label: 'Historique', icon: History },
];

/** In-person pair list filter state. */
export interface PairInPersonFilters {
  types: string[];
  tutorSearch?: string;
  studentSearch?: string;
  establishmentId?: string;
  subjectId?: string;
  /** Predefined period shortcut. Set to 'all' when `customDateFrom`/`customDateTo` are used. */
  dateRange: 'all' | 'today' | 'week' | 'month';
  /** Custom date range — mutually exclusive with `dateRange` != 'all'. */
  customDateFrom?: Date;
  customDateTo?: Date;
}

/** Default in-person pair list filters. */
export const DEFAULT_PAIR_INPERSON_FILTERS: PairInPersonFilters = {
  types: [],
  dateRange: 'month',
  customDateFrom: undefined,
  customDateTo: undefined,
  tutorSearch: undefined,
  studentSearch: undefined,
  establishmentId: undefined,
  subjectId: undefined,
};

/** In-person pair filter group ids. */
export type PairInPersonFilterGroupId =
  | 'filter-type'
  | 'filter-tutor-search'
  | 'filter-student-search'
  | 'filter-establishment'
  | 'filter-subject'
  | 'filter-period'
  | 'filter-date-range';

/** All in-person pair filter group ids (configure-filters panel). */
export const ALL_PAIR_INPERSON_FILTER_GROUP_IDS: PairInPersonFilterGroupId[] = [
  'filter-type',
  'filter-tutor-search',
  'filter-student-search',
  'filter-establishment',
  'filter-subject',
  'filter-period',
  'filter-date-range',
];

/** Human-readable label per in-person pair filter group. */
export const PAIR_INPERSON_FILTER_LABELS: Record<PairInPersonFilterGroupId, string> = {
  'filter-type': 'Type de matching',
  'filter-tutor-search': 'Recherche tuteur',
  'filter-student-search': 'Recherche élève',
  'filter-establishment': 'Établissement',
  'filter-subject': 'Matière',
  'filter-period': 'Période',
  'filter-date-range': 'Date personnalisée',
};

/** Session-column filter state on the matching page. */
export interface MatchingSessionColumnFilters {
  types: string[];
  statuses: string[];
  dateRange: 'all' | 'today' | 'week' | 'month';
  customDateFrom?: Date;
  customDateTo?: Date;
  establishmentId?: string;
  subjectId?: string;
  levelId?: string;
  search?: string;
  recurrenceType?: string;
  tutorId?: string;
  animatorUserId?: string;
}

/** Default session-column filters on the matching page. */
export const DEFAULT_MATCHING_SESSION_FILTERS: MatchingSessionColumnFilters = {
  types: [],
  statuses: [],
  dateRange: 'month',
  customDateFrom: undefined,
  customDateTo: undefined,
  establishmentId: undefined,
  subjectId: undefined,
  levelId: undefined,
  search: undefined,
  recurrenceType: undefined,
  tutorId: undefined,
  animatorUserId: undefined,
};

/** Matching session-column filter group ids. */
export type MatchingSessionFilterGroupId =
  | 'filter-period'
  | 'filter-date-range'
  | 'filter-type'
  | 'filter-status'
  | 'filter-establishment'
  | 'filter-subject'
  | 'filter-level'
  | 'filter-search'
  | 'filter-recurrence'
  | 'filter-animator';

/** All matching session-column filter group ids. */
export const MATCHING_SESSION_FILTER_GROUP_IDS: MatchingSessionFilterGroupId[] = [
  'filter-period',
  'filter-date-range',
  'filter-type',
  'filter-status',
  'filter-establishment',
  'filter-subject',
  'filter-level',
  'filter-search',
  'filter-recurrence',
  'filter-animator',
];

/** Human-readable label per matching session-column filter group. */
export const MATCHING_SESSION_FILTER_GROUP_LABELS: Record<MatchingSessionFilterGroupId, string> = {
  'filter-period': 'Période',
  'filter-date-range': 'Date personnalisée',
  'filter-type': 'Type',
  'filter-status': 'Statut',
  'filter-establishment': 'Établissement',
  'filter-subject': 'Matière',
  'filter-level': 'Niveau',
  'filter-search': 'Recherche participant',
  'filter-recurrence': 'Récurrence',
  'filter-animator': 'Animateur',
};

/** Recurrence type labels for in-person pairs. */
export const PAIR_RECURRENCE_LABELS: Record<string, string> = {
  none: 'Aucune (séance unique)',
  weekly: 'Hebdomadaire',
  ab: 'Bi-mensuelle (A/B)',
};

/** One priority rule in the auto-assignment explanation. */
export interface AutoAssignmentPriorityItem {
  id: string;
  title: string;
  description: string;
  priority: number;
  bgColor: string;
  borderColor: string;
  textColor: string;
  iconColor: string;
  icon: LucideIcon;
}

/** Priority rules shown in the auto-assignment explanation. */
export const AUTO_ASSIGNMENT_PRIORITIES: AutoAssignmentPriorityItem[] = [
  {
    id: 'core_pair',
    icon: Heart,
    title: 'Binôme Cœur',
    description:
      'Élèves et tuteurs non matchés ayant 5 séances terminées ensemble. Ces binômes ont fait leurs preuves et seront priorisés.',
    priority: 1,
    bgColor: 'color-mix(in srgb, rgb(220, 38, 38) 10%, transparent)',
    borderColor: 'rgb(220, 38, 38)',
    textColor: 'rgb(185, 28, 28)',
    iconColor: 'rgb(220, 38, 38)',
  },
  {
    id: 'history',
    icon: History,
    title: 'Matching Historique',
    description:
      "Élèves et tuteurs ayant entre 1 et 4 séances terminées ensemble. L'historique de collaboration est pris en compte.",
    priority: 2,
    bgColor: 'color-mix(in srgb, var(--color-1-nuance-1) 10%, transparent)',
    borderColor: 'var(--color-1-nuance-1)',
    textColor: 'var(--color-1-nuance-1)',
    iconColor: 'var(--color-1-nuance-1)',
  },
  {
    id: 'balanced',
    icon: Scale,
    title: 'Matching Équilibré',
    description:
      'Répartition équilibrée des élèves sur les tuteurs disponibles pour optimiser la charge de travail.',
    priority: 3,
    bgColor: 'color-mix(in srgb, var(--color-2-nuance-1) 10%, transparent)',
    borderColor: 'var(--color-2-nuance-1)',
    textColor: 'var(--color-2-nuance-1)',
    iconColor: 'var(--color-2-nuance-1)',
  },
];

/** Generic badge + icon config (matching status, subjects). */
export interface BadgeConfig {
  label: string;
  icon?: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor?: string;
}

/** Badge config for each matching status. */
export const MATCHING_STATUS_CONFIG: Record<string, BadgeConfig> = {
  matched: {
    label: 'Affecté',
    icon: UserCheck,
    badgeVariant: 'active',
    badgeColor: 'rgb(var(--status-success))',
  },
  unmatched: {
    label: 'Non affecté',
    icon: UserX,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
  },
  'partially-matched': {
    label: 'Partiel',
    icon: Users,
    badgeVariant: 'on-hold',
    badgeColor: 'rgb(var(--status-pending))',
  },
};

/** Matching-status filter options in tutor perspective. */
export const MATCHING_STATUS_FILTER_OPTIONS_TUTOR = [
  { value: 'unmatched', label: 'Non affecté' },
  { value: 'partially-matched', label: 'Partiellement affecté' },
  { value: 'matched', label: 'Affecté' },
] as const;

/** Matching-status filter options in student perspective. */
export const MATCHING_STATUS_FILTER_OPTIONS_STUDENT = [
  { value: 'unmatched', label: 'Non affecté' },
  { value: 'matched', label: 'Affecté' },
] as const;

/** Badge config for matching gender (em dash when unknown). */

/** Shared color for program badges. */
export function getProgramBadgeColor(_programName: string): string {
  return 'var(--color-3-nuance-1)';
}

/** Weekday names keyed by day-of-week number (0/7 = Sunday). */
export const SLOT_DAY_NAMES: Record<number, string> = {
  0: 'dimanche',
  1: 'lundi',
  2: 'mardi',
  3: 'mercredi',
  4: 'jeudi',
  5: 'vendredi',
  6: 'samedi',
  7: 'dimanche',
};

/** Weekday order for availability grouping (Monday first). */
export const AVAILABILITY_DAY_ORDER = [
  'lundi',
  'mardi',
  'mercredi',
  'jeudi',
  'vendredi',
  'samedi',
  'dimanche',
];

/** Shared layout classes for matching availability slot pills. */
const MATCHING_SLOT_PILL_BASE =
  'flex flex-col items-center justify-center gap-0.5 rounded border w-24 px-2 py-1.5 shrink-0 transition-colors';

/** CSS classes for matching slot pills by state. */
export const MATCHING_SLOT_PILL_CLASS = {
  taken: `${MATCHING_SLOT_PILL_BASE} pointer-events-none cursor-not-allowed opacity-60 line-through`,
  nonAffectable: `${MATCHING_SLOT_PILL_BASE} pointer-events-none cursor-not-allowed opacity-60`,
  selected: `${MATCHING_SLOT_PILL_BASE} text-white`,
  default: `${MATCHING_SLOT_PILL_BASE} hover:opacity-80`,
} as const;

/** Inline styles for matching slot pills by state. */
export const MATCHING_SLOT_PILL_STYLE: Record<
  'selected' | 'taken' | 'available' | 'nonAffectable' | 'default',
  CSSProperties
> = {
  selected: {
    backgroundColor: 'var(--color-2-nuance-1)',
    borderColor: 'var(--color-2-nuance-1)',
    color: 'white',
  },
  taken: {
    borderColor: 'color-mix(in srgb, var(--muted-foreground) 30%, transparent)',
    backgroundColor: 'var(--background)',
    color: 'var(--muted-foreground)',
  },
  available: {
    borderColor: 'rgb(var(--status-success))',
    backgroundColor: 'color-mix(in srgb, rgb(var(--status-success)) 10%, transparent)',
    color: 'rgb(var(--status-success))',
  },
  nonAffectable: {
    borderColor: 'rgb(var(--color-error))',
    backgroundColor: 'color-mix(in srgb, rgb(var(--color-error)) 10%, transparent)',
    color: 'rgb(var(--color-error))',
  },
  default: {
    borderColor: 'color-mix(in srgb, var(--color-1-nuance-3) 30%, transparent)',
    backgroundColor: '#fdfaf7',
    color: '#1a1a1a',
  },
};

/** Shared color for level badges. */
export function getLevelBadgeColor(_level: string | number | null | undefined): string {
  return 'var(--color-2-nuance-2)';
}

/** Shared icon and color for level badges. */
export const LEVEL_BADGE_CONFIG = {
  icon: GraduationCap,
  variant: 'outlined' as const,
  color: 'var(--color-2-nuance-2)',
};

/** Shared icon and color for program badges. */
export const PROGRAM_DISPLAY_CONFIG = {
  icon: BookOpen,
  variant: 'outlined' as const,
  color: 'var(--color-3-nuance-1)',
};

/** Shared icon and color for subject badges. */
export const SUBJECT_DISPLAY_CONFIG = {
  icon: FlaskConical,
  variant: 'outlined' as const,
  color: 'var(--color-1-nuance-2)',
};

/** Shared color for subject badges. */
export function getSubjectBadgeColor(_subjectName: string): string {
  return 'var(--color-1-nuance-2)';
}

/** Filter state for the remote matching list. */
export interface RemoteMatchingFilters {
  mode?: 'tutor' | 'student';
  program?: string;
  status?: string;
  search?: string;
  establishment?: string;
  gender?: 'male' | 'female' | 'other';
  partner?: boolean;
  subject?: string;
}

/** Default remote matching list filters. */
export const DEFAULT_REMOTE_MATCHING_FILTERS: RemoteMatchingFilters = {
  mode: 'tutor',
};

/** Remote matching filter group ids. */
export type RemoteMatchingFilterGroupId =
  | 'filter-mode'
  | 'filter-program'
  | 'filter-subject'
  | 'filter-status'
  | 'filter-search'
  | 'filter-establishment';

/** All remote matching filter group ids. */
export const REMOTE_MATCHING_FILTER_GROUP_IDS: RemoteMatchingFilterGroupId[] = [
  'filter-mode',
  'filter-program',
  'filter-subject',
  'filter-status',
  'filter-search',
  'filter-establishment',
];

/** Human-readable label per remote matching filter group. */
export const REMOTE_MATCHING_FILTER_LABELS: Record<RemoteMatchingFilterGroupId, string> = {
  'filter-mode': 'Mode',
  'filter-program': 'Programme',
  'filter-subject': 'Matière',
  'filter-status': 'Statut de matching',
  'filter-search': 'Recherche',
  'filter-establishment': 'Établissement',
};

/** Matching perspective toggle (tutor vs student). */
export const MATCHING_MODE_OPTIONS = [
  {
    value: 'tutor' as const,
    get label() {
      return getRoleLabel('tutor');
    },
  },
  {
    value: 'student' as const,
    get label() {
      return getRoleLabel('student');
    },
  },
];

/** Matching-status options on the remote matching list. */
export const MATCHING_LIST_STATUS_OPTIONS = [
  { value: 'unmatched', label: 'Non affecté' },
  { value: 'matched', label: 'Affecté / matching confirmé' },
] as const;

/** Search placeholders per matching perspective. */
export const MATCHING_SEARCH_PLACEHOLDERS: Record<'tutor' | 'student', string> = {
  get tutor() {
    return `Rechercher ${getRoleLabel('tutor').toLowerCase()}`;
  },
  get student() {
    return `Rechercher ${getRoleLabel('student').toLowerCase()}`;
  },
};

/** Role-aware copy for each matching perspective. */
export const MATCHING_PERSPECTIVE_LABELS = {
  tutor: {
    get singular() {
      return getRoleLabel('tutor').toLowerCase();
    },
    get plural() {
      return getRoleLabelPlural('tutor').toLowerCase();
    },
    get title() {
      return getRoleLabelPlural('tutor');
    },
    get resultLabel() {
      return `${getRoleLabel('tutor').toLowerCase()}(s)`;
    },
    get createTooltip() {
      return `Créer un ${getRoleLabel('tutor').toLowerCase()}`;
    },
  },
  student: {
    get singular() {
      return getRoleLabel('student').toLowerCase();
    },
    get plural() {
      return getRoleLabelPlural('student').toLowerCase();
    },
    get title() {
      return getRoleLabelPlural('student');
    },
    get resultLabel() {
      return `${getRoleLabel('student').toLowerCase()}(s)`;
    },
    get createTooltip() {
      return `Créer un ${getRoleLabel('student').toLowerCase()}`;
    },
  },
};

/** CSV column titles for the tutor-perspective matching export. */
export const MATCHING_CSV_HEADERS_TUTOR = [
  'Nom complet',
  'Programme',
  'Genre',
  'Matière',
  'Niveau',
  'Capacité',
  'Statut',
  'Affectabilité',
  'Engagement valorisé',
] as const;

/** CSV column titles for the student-perspective matching export. */
export const MATCHING_CSV_HEADERS_STUDENT = [
  'Nom complet',
  'Programme',
  'Genre',
  'Matière',
  'Niveau',
  'Statut',
  'Affectabilité',
  'Partenaire',
] as const;

/** Filter-group labels on the matching tab chips. */
export const MATCHING_TAB_FILTER_GROUP_LABELS = {
  matchingStatus: 'Statut de matching',
  assignability: 'Affectabilité',
} as const;

/** Max badges shown before a +N overflow chip. */
export const MATCHING_BADGE_MAX_VISIBLE = 3;

/** Page size for matching candidate lists. */
export const MATCHING_CANDIDATES_PAGE_SIZE = 10;
